/**
 * This is the NATIONAL STANDARD PAGE
 *
 * National Standard pages will always have a unique slug for this content-type
 * Can have multiple parents but all will be of the same content-type NATIONAL STANDARD type
 *
 * For the HOME PAGE (slug `/` ) please see the file /pages/index.tsx
 */
import React from 'react';
import { GetStaticProps } from 'next';
import { apolloClient, setApolloHeaders } from '@src/services/apollo';
import {
  pageNationalStandardQuery,
  pageNationalStandardPart1ContentBlocksQuery,
  pageNationalStandardPart2ContentBlocksQuery,
  pageNationalStandardPart3ContentBlocksQuery,
  pageNationalStandardPart4ContentBlocksQuery
} from '@src/graphql/queries/pageNationalStandard.query';
import { pageNationalStandardPathsQuery } from '@src/graphql/queries/pageNationalStandardPaths.query';
import PageNationalStandardTemplate from '@src/templates/PageNationalStandard';
import {
  GlobalSettingsQuery,
  NationalBasePageQuery,
  PageNationalStandardPart1ContentBlocksQuery,
  PageNationalStandardPart2ContentBlocksQuery,
  PageNationalStandardPart3ContentBlocksQuery,
  PageNationalStandardPart4ContentBlocksQuery,
  PageNationalStandardPathsQuery,
  PageNationalStandardQuery,
  PageNationalStandardQueryVariables
} from '@src/graphql/gql-types';
import { flattenPagePaths, getSlugsFromArray } from '@src/utils/url';
import {
  globalSettingsQuery,
  nationalBasePageQuery
} from '@src/graphql/queries';
import { NationalStandardPageProps } from '@src/types/page';
import { getValidPathsOnly } from '@src/utils/build/paths';
import { mergeContentBlocks } from '@src/utils/helpers';
import { base64EncodeHero } from '@src/utils/build/base64EncodeHero';
import { PATHS } from '@src/constants/site';

const NationalStandardPage: React.FC<NationalStandardPageProps> = (props) => {
  return <PageNationalStandardTemplate {...props} />;
};

export default NationalStandardPage;

/**
 * Build static paths
 * - Based on the GraphQL query `pageNationalStandardPaths.query` this should return upto __4 levels__ of National Standard pages.
 * - Please confirm with checking in the GraphQL query for what is returned.
 * - This uses nextJS [catch-all-routes](https://nextjs.org/docs/routing/dynamic-routes#catch-all-routes)
 * @returns path params
 */
export const getStaticPaths = async () => {
  const pageQueryResponse =
    await apolloClient.query<PageNationalStandardPathsQuery>({
      query: pageNationalStandardPathsQuery
    });

  if (pageQueryResponse?.errors) {
    console.error(pageQueryResponse.errors);
  }

  const pages =
    pageQueryResponse.data?.pageNationalStandardCollection?.items || [];

  const paths = pages
    .filter((page) => !!page?.slug)
    .filter((page) => page?.slug !== '/')
    .map((page) => {
      /**
       * Get an array of the page paths (This flattens the nested parent path into a usable array) */
      const arrayOfPages = flattenPagePaths(page);
      /**
       * Now extract the slug names in to an array : ['page-a','page-b','page-c']
       * - This is used for the nextJS catch-all-routes dynamic path, which requires an array */
      const slugCatchAllArray = getSlugsFromArray(arrayOfPages);

      return {
        params: {
          slug_national_standard: slugCatchAllArray
        }
      };
    })
    // Remove the paths to be handled by the find your community branch
    .filter(
      (path) =>
        path.params.slug_national_standard[0] !== PATHS.FIND_YOUR_COMMUNITY
    );

  const validPaths = getValidPathsOnly(paths);

  return {
    paths: validPaths,
    fallback: false
  };
};

/**
 * PLEASE NOTE!  This does not control the HOME PAGE ('/')
 * For the home page see see the file /pages/index.tsx
 */
export const getStaticProps: GetStaticProps = async ({ preview, params }) => {
  /**
   * Always get the last item in the array, this will be the current page to display */
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/at
  const pageSlug = params?.slug_national_standard?.at(-1);

  if (preview) {
    setApolloHeaders(process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW_TOKEN);
  }

  const [
    globalSettingsQueryResponse,
    nationalBasePageQueryResponse,
    pageQueryResponse,
    pageNationalStandardPart1ContentBlocksQueryResponse,
    pageNationalStandardPart2ContentBlocksQueryResponse,
    pageNationalStandardPart3ContentBlocksQueryResponse,
    pageNationalStandardPart4ContentBlocksQueryResponse
  ] = await Promise.all([
    apolloClient.query<GlobalSettingsQuery>({
      query: globalSettingsQuery,
      variables: {
        preview: preview
      }
    }),
    apolloClient.query<NationalBasePageQuery>({
      query: nationalBasePageQuery,
      variables: {
        preview: preview
      }
    }),
    apolloClient.query<
      PageNationalStandardQuery,
      PageNationalStandardQueryVariables
    >({
      query: pageNationalStandardQuery,
      variables: { slug: pageSlug, preview: preview }
    }),
    apolloClient.query<
      PageNationalStandardPart1ContentBlocksQuery,
      PageNationalStandardQueryVariables
    >({
      query: pageNationalStandardPart1ContentBlocksQuery,
      variables: { slug: pageSlug, preview: preview }
    }),
    apolloClient.query<
      PageNationalStandardPart2ContentBlocksQuery,
      PageNationalStandardQueryVariables
    >({
      query: pageNationalStandardPart2ContentBlocksQuery,
      variables: { slug: pageSlug, preview: preview }
    }),
    apolloClient.query<
      PageNationalStandardPart3ContentBlocksQuery,
      PageNationalStandardQueryVariables
    >({
      query: pageNationalStandardPart3ContentBlocksQuery,
      variables: { slug: pageSlug, preview: preview }
    }),
    apolloClient.query<
      PageNationalStandardPart4ContentBlocksQuery,
      PageNationalStandardQueryVariables
    >({
      query: pageNationalStandardPart4ContentBlocksQuery,
      variables: { slug: pageSlug, preview: preview }
    })
  ]);

  if (globalSettingsQueryResponse?.errors) {
    console.error(globalSettingsQueryResponse.errors);
  }

  if (nationalBasePageQueryResponse?.errors) {
    console.error(nationalBasePageQueryResponse.errors);
  }

  if (pageQueryResponse?.errors) {
    console.error(pageQueryResponse.errors);
  }

  if (pageNationalStandardPart1ContentBlocksQueryResponse?.errors) {
    console.error(pageNationalStandardPart1ContentBlocksQueryResponse.errors);
  }

  if (pageNationalStandardPart2ContentBlocksQueryResponse?.errors) {
    console.error(pageNationalStandardPart2ContentBlocksQueryResponse.errors);
  }

  if (pageNationalStandardPart3ContentBlocksQueryResponse?.errors) {
    console.error(pageNationalStandardPart3ContentBlocksQueryResponse.errors);
  }

  if (pageNationalStandardPart4ContentBlocksQueryResponse?.errors) {
    console.error(pageNationalStandardPart4ContentBlocksQueryResponse.errors);
  }

  const items = mergeContentBlocks([
    pageNationalStandardPart1ContentBlocksQueryResponse?.data,
    pageNationalStandardPart2ContentBlocksQueryResponse?.data,
    pageNationalStandardPart3ContentBlocksQueryResponse?.data,
    pageNationalStandardPart4ContentBlocksQueryResponse?.data
  ]);

  const pagePayload = await base64EncodeHero(pageQueryResponse?.data);

  const templatePropsData: NationalStandardPageProps = {
    globalSettingsPayload: globalSettingsQueryResponse?.data,
    nationalBasePagePayload: nationalBasePageQueryResponse?.data,
    pagePayload: pagePayload as PageNationalStandardQuery,
    pageContentBlocks: items
  };

  return {
    props: {
      ...templatePropsData
    }
  };
};
